import React, { Fragment, useEffect, useState } from 'react';
import styled from 'styled-components';
import { filter } from 'ramda';
import Form from '~~elements/Form';
import Button from '~~elements/Button';
import toast from '~~elements/Toast';
import IconAttachment from '~~elements/static/IconAttachment.svg';
import { TeamInSeasonRegistryResource } from '~~apis/resource';
import { parsePDFData } from '../service';
import UTRMultilingual from '../../Multilingual';
import RMultilingual from '../../../Tournament/Registry/Multilingual';
import { useRegistryDispatch } from '../../useUserTournamentRegistry';

const StyledDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  .description {
    margin: 2rem 0;
    white-space: pre-line;
    letter-spacing: 2px;
    fint-size: 0.9rem;
    text-align: start;
  }

  input:disabled {
    opacity: 0.8;
  }
`;

const SingleTargetDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: start;
  align-self: flex-start;
  border: 1px solid #eee;
  border-radius: 2px;
`;

const PDFDiv = styled.div`
  line-height: 1;
  padding: 1rem 0;
  text-align: start;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: start;
  align-self: flex-start;
  > button {
    align-self: center;
    margin: 0.2rem;
  }
`;

const PDFSection = ({ teamInRegistry, registry, disabled }) => {
  const dispatch = useRegistryDispatch();

  const [PDFData, setPDFData] = useState(parsePDFData(teamInRegistry, registry));

  const [uploadPendingFile, setUploadPendingFile] = useState(null);
  const [targetFile, setTargetFile] = useState({
    type: '',
    index: -1,
    columnName: '',
  });

  const [attachmentRefresh, setAttachmentRefresh] = useState(0);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setPDFData(parsePDFData(teamInRegistry, registry));
  }, [registry, teamInRegistry]);

  const handleFileOnChange = (target) => (e) => {
    e.preventDefault();
    setUploadPendingFile(null);
    setTargetFile({
      type: '',
      index: -1,
      columnName: '',
    });

    const file = e.target.files[0];
    if (!file) {
      return;
    }

    const { size: fileSize, type } = file;

    if (type !== 'application/pdf' || fileSize > 1024 * 1024 * 25) {
      toast(RMultilingual(`ERROR.DOCUMENT`), { status: 'error', second: 2 });
      setAttachmentRefresh((pre) => pre + 1);
      return;
    }

    setUploadPendingFile(file);
    setTargetFile({ ...target });
  };

  const handleFileUpload = () => {
    let staffTitle = '';
    if (targetFile.type === 'staff') {
      staffTitle = filter((s) => s.pdfColumns.length > 0, PDFData.staffs)[targetFile.index].title;
    }

    const data = new FormData();
    data.append('type', targetFile.type);
    data.append('staff_title', staffTitle);
    data.append('player_index', targetFile.index);
    data.append('column_name', targetFile.columnName);
    data.append('action', 'UPLOAD');
    data.append('pdf', uploadPendingFile);

    setUploading(true);
    TeamInSeasonRegistryResource.uploadPDF({
      registryUniqid: registry.uniqid,
      teamInRegistryUniqid: teamInRegistry.uniqid,
      data,
    })
      .then(({ data: newTeamInRegistry }) => {
        dispatch({
          type: 'UPDATE_TEAM_IN_REGISTRY',
          payload: newTeamInRegistry,
        });
        toast(UTRMultilingual(`SUCCESS.UPLOAD`), { status: 'success' });
        setUploadPendingFile(null);
        setTargetFile({
          type: '',
          index: -1,
          columnName: '',
        });
        setAttachmentRefresh((pre) => pre + 1);
      })
      .catch(() => {
        toast(UTRMultilingual(`COMMON.UNKNOWN_ERROR`), { status: 'error' });
      })
      .finally(() => {
        setUploading(false);
      });
  };

  const handleFileRemove = (target) => () => {
    let staffTitle = '';
    if (target.type === 'staff') {
      staffTitle = filter((s) => s.pdfColumns.length > 0, PDFData.staffs)[target.index].title;
    }

    const data = new FormData();
    data.append('type', target.type);
    data.append('staff_title', staffTitle);
    data.append('player_index', target.index);
    data.append('column_name', target.columnName);
    data.append('action', 'DELETE');
    data.append('pdf', null);

    setUploading(true);
    TeamInSeasonRegistryResource.uploadPDF({
      registryUniqid: registry.uniqid,
      teamInRegistryUniqid: teamInRegistry.uniqid,
      data,
    })
      .then(({ data: newTeamInRegistry }) => {
        dispatch({
          type: 'UPDATE_TEAM_IN_REGISTRY',
          payload: newTeamInRegistry,
        });
        toast(UTRMultilingual(`SUCCESS.DELETE`), { status: 'success' });
      })
      .catch(() => {
        toast(UTRMultilingual(`COMMON.UNKNOWN_ERROR`), { status: 'error' });
      })
      .finally(() => {
        setUploading(false);
      });
  };

  const cancelFileUpload = () => {
    setAttachmentRefresh((pre) => pre + 1);
    setUploadPendingFile(null);
    setTargetFile({
      type: '',
      index: -1,
      columnName: '',
    });
  };

  const getPendingActions = () => (
    <Fragment>
      <Button width="unset" color="red" onClick={handleFileUpload}>
        {RMultilingual(`FORM.UPLOAD`)}
      </Button>
      <Button width="unset" color="transparentWithBorder" onClick={cancelFileUpload}>
        {RMultilingual(`COMMON.CANCEL`)}
      </Button>
    </Fragment>
  );

  return (
    <StyledDiv>
      <Form onSubmit={(e) => e.preventDefault()} className="lg">
        <div className="box">
          <h3>{UTRMultilingual(`CUSTOM.TEAM_PDF`)}</h3>
          <div className="input-field lg">
            <div className="input-list">
              {PDFData.pdfColumns.map((column, index) => (
                <SingleTargetDiv key={index}>
                  <PDFDiv>
                    <label>
                      {column.name}
                      {column.is_required && <i>*</i>}
                    </label>
                    {column.value && (
                      <span>
                        <img src={IconAttachment} style={{ width: '1rem', height: '1rem' }} alt="attachIcon" />
                        <a href={column.url} target="_blank" rel="noopener noreferrer">
                          {UTRMultilingual(`COMMON.VIEW`)}
                        </a>
                        <Button
                          mini
                          color="transparent"
                          width="unset"
                          onClick={handleFileRemove({ type: 'team', index: -1, columnName: column.name })}
                          disabled={disabled}
                        >
                          x
                        </Button>
                      </span>
                    )}
                    <input
                      key={attachmentRefresh}
                      type="file"
                      accept="application/pdf"
                      onChange={handleFileOnChange({ type: 'team', index: -1, columnName: column.name })}
                      disabled={disabled || uploading || !!uploadPendingFile}
                    />
                    {targetFile.type === 'team' && targetFile.columnName === column.name && getPendingActions()}
                  </PDFDiv>
                </SingleTargetDiv>
              ))}
            </div>
          </div>
          <h3>{UTRMultilingual(`CUSTOM.STAFF_PDF`)}</h3>
          <div className="input-field lg">
            <div className="input-list">
              {filter((s) => s.pdfColumns.length > 0, PDFData.staffs).map((staff, staffIndex) => (
                <SingleTargetDiv key={staffIndex}>
                  <strong>{staff.title}</strong>
                  {staff.pdfColumns.map((column) => (
                    <PDFDiv key={column.name}>
                      <label>
                        {column.name}
                        {column.is_required && <i>*</i>}
                      </label>
                      {column.value && (
                        <span>
                          <img src={IconAttachment} style={{ width: '1rem', height: '1rem' }} alt="attachIcon" />
                          <a href={column.url} target="_blank" rel="noopener noreferrer">
                            {UTRMultilingual(`COMMON.VIEW`)}
                          </a>
                          <Button
                            mini
                            color="transparent"
                            width="unset"
                            onClick={handleFileRemove({ type: 'staff', index: staffIndex, columnName: column.name })}
                            disabled={disabled}
                          >
                            x
                          </Button>
                        </span>
                      )}
                      <input
                        key={attachmentRefresh}
                        type="file"
                        accept="application/pdf"
                        onChange={handleFileOnChange({ type: 'staff', index: staffIndex, columnName: column.name })}
                        disabled={disabled || uploading || !!uploadPendingFile}
                      />
                      {targetFile.type === 'staff' &&
                        targetFile.index === staffIndex &&
                        targetFile.columnName === column.name &&
                        getPendingActions()}
                    </PDFDiv>
                  ))}
                </SingleTargetDiv>
              ))}
            </div>
          </div>
          <h3>{UTRMultilingual(`CUSTOM.PLAYER_PDF`)}</h3>
          <div className="input-field lg">
            {filter((c) => c.type === 'PDF', registry.player_columns).length > 0 && (
              <div className="input-list">
                {PDFData.players.map((player, playerIndex) => (
                  <SingleTargetDiv key={playerIndex}>
                    <strong>{`${player.name} #${player.number}`}</strong>
                    {player.pdfColumns.map((column) => (
                      <PDFDiv key={column.name}>
                        <label>
                          {column.name}
                          {column.is_required && <i>*</i>}
                        </label>
                        {column.value && (
                          <span>
                            <img src={IconAttachment} style={{ width: '1rem', height: '1rem' }} alt="attachIcon" />
                            <a href={column.url} target="_blank" rel="noopener noreferrer">
                              {UTRMultilingual(`COMMON.VIEW`)}
                            </a>
                            <Button
                              mini
                              color="transparent"
                              width="unset"
                              onClick={handleFileRemove({
                                type: 'player',
                                index: playerIndex,
                                columnName: column.name,
                              })}
                              disabled={disabled}
                            >
                              x
                            </Button>
                          </span>
                        )}
                        <input
                          key={attachmentRefresh}
                          type="file"
                          accept="application/pdf"
                          onChange={handleFileOnChange({ type: 'player', index: playerIndex, columnName: column.name })}
                          disabled={disabled || uploading || !!uploadPendingFile}
                        />
                        {targetFile.type === 'player' &&
                          targetFile.index === playerIndex &&
                          targetFile.columnName === column.name &&
                          getPendingActions()}
                      </PDFDiv>
                    ))}
                  </SingleTargetDiv>
                ))}
              </div>
            )}
          </div>
        </div>
      </Form>
    </StyledDiv>
  );
};

export default PDFSection;
